// Rechnung an den Eigentümer weiterleiten: Vorbereitung des Verfassen-Formulars
// (Eigentümer-Mail + Vorlage + Drive-PDF) und Nachbereitung nach dem Versand
// (weitergeleitet_am, Rechnungs-Timeline, Eigentümer-Aktivität).
import { supabase } from '../supabase/client';
import { addTimelineEvent } from './timeline';
import { logAktivitaet } from '../kontakte/api';
import { eigentuemerAnzeigename } from '../eigentuemer/anzeige';

// Stabiler Identifier auf der Vorlage „Rechnungsweiterleitung" (robust gegen Umbenennen).
const WEITERLEITUNG_TRIGGER = 'rechnung_weiterleitung';

// Google-Drive-File-ID aus datei_url (".../file/d/<ID>/..."). null bei Nicht-Drive-URLs.
export function driveFileIdAusUrl(url?: string | null): string | null {
  if (!url) return null;
  const m = url.match(/\/file\/d\/([^/]+)/);
  return m ? m[1] : null;
}

export interface WeiterleitungCompose {
  ok: true;
  rechnungId: string;
  rechnungsnummer: string;
  ownerId: string;
  ownerEmail: string;
  ownerName: string;
  betreff: string;
  html: string;
  driveFileId: string;
  dateiname: string;
}

export type WeiterleitungFehlerGrund =
  | 'nicht_gefunden' | 'kein_eigentuemer' | 'keine_email' | 'keine_datei' | 'keine_vorlage';

export interface WeiterleitungFehler {
  ok: false;
  grund: WeiterleitungFehlerGrund;
  text: string;
}

export type WeiterleitungErgebnis = WeiterleitungCompose | WeiterleitungFehler;

// Alles laden, was das Verfassen-Formular für die Weiterleitung an den Eigentümer braucht.
// `ausPool` = der Beleg liegt in rechnungen_unklar (KI-Pool). Dort steht dieselbe PDF und
// derselbe Eigentümer — nur gebucht ist er noch nicht. Weiterleiten muss trotzdem gehen:
// gerade Mahnungen und Prüffälle will man dem Eigentümer zeigen (User-Meldung 2026-08-11).
export async function baueWeiterleitungCompose(
  rechnungId: string, ausPool = false,
): Promise<WeiterleitungErgebnis> {
  const { data: r } = await supabase.from(ausPool ? 'rechnungen_unklar' : 'rechnungen')
    .select('id, rechnungsnummer, datei_url, eigentuemer_id')
    .eq('id', rechnungId).maybeSingle();
  if (!r) return { ok: false, grund: 'nicht_gefunden', text: 'Rechnung nicht gefunden.' };
  if (!r.eigentuemer_id) return { ok: false, grund: 'kein_eigentuemer', text: 'Dieser Rechnung ist kein Eigentümer zugeordnet.' };

  // Eigentümer + E-Mail auflösen: Haupt-Spalte, sonst kontakt_kanaele (Zweit-Adressen).
  const { data: eig } = await supabase.from('eigentuemer')
    .select('id, anrede, vorname, nachname, firma_name, email').eq('id', r.eigentuemer_id).maybeSingle();
  if (!eig) return { ok: false, grund: 'kein_eigentuemer', text: 'Eigentümer nicht gefunden.' };
  let email = (eig.email || '').trim();
  if (!email) {
    const { data: kanal } = await supabase.from('kontakt_kanaele')
      .select('wert').eq('owner_type', 'eigentuemer').eq('owner_id', eig.id).eq('kanal', 'email')
      .order('ist_primaer', { ascending: false }).limit(1).maybeSingle();
    email = (kanal?.wert || '').trim();
  }
  if (!email) return { ok: false, grund: 'keine_email', text: 'Für den Eigentümer ist keine E-Mail-Adresse hinterlegt.' };

  const driveFileId = driveFileIdAusUrl(r.datei_url);
  if (!driveFileId) return { ok: false, grund: 'keine_datei', text: 'Zu dieser Rechnung liegt keine Google-Drive-PDF vor.' };

  const { data: vorlage } = await supabase.from('email_vorlagen')
    .select('betreff_template, html_template').eq('ki_trigger', WEITERLEITUNG_TRIGGER).maybeSingle();
  if (!vorlage) return { ok: false, grund: 'keine_vorlage', text: 'Die Vorlage „Rechnungsweiterleitung" wurde nicht gefunden.' };

  const anzeige = eigentuemerAnzeigename(eig);
  const ownerName = anzeige === '—' ? email : anzeige;
  // Pool-Belege haben oft (noch) keine erkannte Nummer — dann dieselbe Kennung wie in der
  // Liste („POOL-99"), damit Dateiname und Protokoll zum Bildschirm passen.
  const nr = r.rechnungsnummer
    || (ausPool ? `POOL-${String(r.id).slice(0, 4).toUpperCase()}` : 'Rechnung');
  const dateiname = `Rechnung_${nr}.pdf`.replace(/[^\wäöüÄÖÜß.\-]+/g, '_');

  return {
    ok: true,
    rechnungId: r.id,
    rechnungsnummer: nr,
    ownerId: eig.id,
    ownerEmail: email,
    ownerName,
    betreff: vorlage.betreff_template || 'Weiterleitung der Rechnung',
    html: vorlage.html_template || '',
    driveFileId,
    dateiname,
  };
}

// Nach erfolgreichem Versand: Rechnung markieren + Timeline + Eigentümer-Aktivität.
// Fehler in Timeline/Aktivität dürfen die Markierung nicht kippen (best effort).
export async function markiereWeitergeleitet(opts: {
  rechnungId: string;
  rechnungsnummer: string;
  ownerId: string;
  ownerEmail: string;
  userId?: string | null;
  /** Beleg aus dem KI-Pool (rechnungen_unklar) statt aus rechnungen. */
  ausPool?: boolean;
}): Promise<void> {
  const jetzt = new Date().toISOString();
  const { error } = await supabase.from(opts.ausPool ? 'rechnungen_unklar' : 'rechnungen')
    .update({ weitergeleitet_am: jetzt }).eq('id', opts.rechnungId);
  if (error) throw error;

  // Die Rechnungs-Timeline hängt per Fremdschlüssel an `rechnungen` — für einen Pool-Beleg
  // gibt es dort keine Zeile, ein Eintrag würde scheitern. Die Spur bleibt über
  // weitergeleitet_am am Beleg und die Aktivität beim Eigentümer erhalten.
  if (opts.userId && !opts.ausPool) {
    try {
      await addTimelineEvent(opts.rechnungId, 'et_weitergeleitet', opts.userId, `Rechnung per E-Mail an ${opts.ownerEmail} weitergeleitet`);
    } catch (e) { console.warn('Timeline-Eintrag (ET-Weiterleitung) fehlgeschlagen', e); }
  }
  try {
    await logAktivitaet('eigentuemer', opts.ownerId, {
      typ: 'mail',
      richtung: 'ausgehend',
      betreff: `Rechnung ${opts.rechnungsnummer} weitergeleitet`,
      notiz: `Beleg per E-Mail an ${opts.ownerEmail} weitergeleitet.`,
    });
  } catch (e) { console.warn('Eigentümer-Aktivität (ET-Weiterleitung) fehlgeschlagen', e); }
}
